import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Content,
  Header,
  Link,
  Page,
  Progress,
} from '@backstage/core-components';
import {
  Tab,
  Tabs,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useInsights } from '../catalog/pipeline/useInsights';
import { updatedLabel } from '../home/updatedLabel';
import { visuallyHidden } from '../visuallyHidden';
import {
  entityPageOf,
  formatShare,
  rowIdOf,
  sourceNotice,
  toneOf,
  type Check,
  type CheckId,
  type Mark,
  type ScorecardsSnapshot,
  type ScoredService,
  type Tone,
} from './scorecards';
import { RepositoriesView } from './RepositoriesView';
import { CollapsedNotices } from './CollapsedNotices';

/** The figures above the table: the target picture's four. */
const FIGURES: { check: CheckId; label: string }[] = [
  { check: 'owner', label: 'Owner resolves' },
  { check: 'statusProbe', label: 'Status probe' },
  { check: 'runbook', label: 'Runbook linked' },
];

const TONE_COLOUR: Record<Tone, string> = {
  good: '#2EA043',
  fair: '#D29922',
  poor: '#F85149',
};

/** Each state in a shape of its own, so colour is never the only difference. */
const MARK: Record<
  Mark['state'],
  { glyph: string; word: string; colour: string }
> = {
  pass: { glyph: '✓', word: 'passed', colour: '#2EA043' },
  fail: { glyph: '✕', word: 'failed', colour: '#F85149' },
  unknown: { glyph: '?', word: 'not asked', colour: 'var(--ui-text-muted)' },
};

const useStyles = makeStyles(theme => ({
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
    minWidth: 0,
  },
  figures: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    // On a phone two to a line, rather than one per line down the screen.
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    border: '1px solid var(--ui-border)',
    borderRadius: 8,
    background: 'var(--ui-bg-elevated)',
    overflow: 'hidden',
  },
  figure: {
    padding: theme.spacing(1.5, 2),
    minWidth: 0,
    borderRight: '1px solid var(--ui-border)',
    '&:last-child': { borderRight: 'none' },
    [theme.breakpoints.down('xs')]: {
      '&:nth-child(2)': { borderRight: 'none' },
      '&:nth-child(-n+2)': { borderBottom: '1px solid var(--ui-border)' },
    },
  },
  label: { fontSize: '0.8rem', color: 'var(--ui-text-muted)' },
  value: {
    fontSize: '1.5rem',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.4,
    color: 'var(--ui-text)',
  },
  sub: { fontSize: '0.8rem', color: 'var(--ui-text-muted)' },
  none: {
    fontSize: '0.9rem',
    color: 'var(--ui-text-muted)',
    lineHeight: 2.1,
  },
  // The table scrolls sideways inside its own box on a narrow screen, so the
  // page itself never does. Positioned, because the words kept for screen
  // readers are positioned absolutely: without it they hang on a box of the
  // shell's, outside this one's scrolling, and widen the page (AC-8).
  tableBox: {
    position: 'relative',
    border: '1px solid var(--ui-border)',
    borderRadius: 8,
    background: 'var(--ui-bg-elevated)',
    overflowX: 'auto',
    minWidth: 0,
  },
  table: {
    width: '100%',
    minWidth: 720,
    borderCollapse: 'collapse',
    fontSize: '0.875rem',
    color: 'var(--ui-text)',
    '& th, & td': {
      padding: theme.spacing(1.25, 1.5),
      borderBottom: '1px solid var(--ui-border)',
      textAlign: 'left',
    },
    '& thead th': {
      fontWeight: 400,
      fontSize: '0.8rem',
      color: 'var(--ui-text-muted)',
    },
    '& tbody tr:last-child > *': { borderBottom: 'none' },
  },
  checkHead: { width: 44 },
  serviceCell: { minWidth: 200 },
  serviceName: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontWeight: 600,
  },
  toggle: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    padding: 0,
    border: 'none',
    borderRadius: 4,
    background: 'transparent',
    color: 'var(--ui-text-muted)',
    cursor: 'pointer',
    font: 'inherit',
    '&:hover': { background: 'var(--ui-bg-muted)' },
    '&:focus-visible': {
      outline: '2px solid var(--ui-primary)',
      outlineOffset: 1,
    },
  },
  chevron: {
    display: 'inline-block',
    transition: 'transform 120ms',
    '&[data-open="true"]': { transform: 'rotate(90deg)' },
  },
  score: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    whiteSpace: 'nowrap',
    minWidth: 150,
  },
  track: {
    flex: '1 1 auto',
    maxWidth: 110,
    height: 6,
    borderRadius: 3,
    background: 'var(--ui-bg-muted)',
    overflow: 'hidden',
  },
  fill: { height: '100%', borderRadius: 3 },
  scoreText: {
    fontVariantNumeric: 'tabular-nums',
    color: 'var(--ui-text-muted)',
  },
  mark: {
    display: 'block',
    textAlign: 'center',
    fontWeight: 700,
    cursor: 'default',
  },
  highlighted: {
    '& > *': { background: 'var(--ui-nav-active-bg)' },
  },
  details: {
    margin: 0,
    padding: theme.spacing(0.5, 0, 0.5, 4),
    listStyle: 'none',
    display: 'grid',
    gap: theme.spacing(0.5),
    '& li': { display: 'flex', gap: theme.spacing(1), alignItems: 'baseline' },
  },
  detailCheck: { fontWeight: 600, whiteSpace: 'nowrap' },
  // Above the table rather than beneath it: the columns are numbered, and the
  // numbers are read before the marks under them.
  legend: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: theme.spacing(0.5, 2),
    margin: 0,
    padding: theme.spacing(1.25, 2),
    listStyle: 'none',
    border: '1px solid var(--ui-border)',
    borderRadius: 8,
    background: 'var(--ui-bg-elevated)',
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
    '& b': { color: 'var(--ui-text)', marginRight: 4 },
  },
  // One row on a desktop: the fields share the width, and a long choice is
  // cut short inside its field rather than widening it. Two to a row below.
  filters: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr)) auto',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  select: { minWidth: 0 },
  count: {
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
    whiteSpace: 'nowrap',
  },
  hidden: visuallyHidden,
  message: { color: 'var(--ui-text-muted)', padding: theme.spacing(2, 0) },
  tabs: {
    minHeight: 40,
    marginBottom: theme.spacing(2.5),
    borderBottom: '1px solid var(--ui-border)',
  },
  tab: {
    minHeight: 40,
    minWidth: 0,
    padding: theme.spacing(0, 2),
    textTransform: 'none',
    fontSize: '0.9rem',
    color: 'var(--ui-text-muted)',
    // By the class handed to the tab for its selected state, never by MUI's
    // own name for it (CLAUDE.md).
    '&$tabSelected': { color: 'var(--ui-text)' },
  },
  tabSelected: {},
}));

const Figure = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => {
  const classes = useStyles();
  return (
    <div className={classes.figure} role="group" aria-label={label}>
      <div className={classes.label}>{label}</div>
      {children}
    </div>
  );
};

const Figures = ({ snapshot }: { snapshot: ScorecardsSnapshot }) => {
  const classes = useStyles();
  const { readiness, checks, services } = snapshot.summary;
  return (
    <section aria-label="Figures" className={classes.figures}>
      <Figure label="Avg. readiness">
        {readiness !== null ? (
          <>
            <div className={classes.value}>{formatShare(readiness, 1)}</div>
            <div className={classes.sub}>
              over {services} {services === 1 ? 'service' : 'services'}
            </div>
          </>
        ) : (
          <div className={classes.none}>No check was asked</div>
        )}
      </Figure>
      {FIGURES.map(({ check, label }) => {
        const { passed, asked } = checks[check];
        return (
          <Figure key={check} label={label}>
            {asked > 0 ? (
              <>
                <div className={classes.value}>
                  {formatShare(passed, asked)}
                </div>
                <div className={classes.sub}>
                  {passed} of {asked} {asked === 1 ? 'service' : 'services'}
                </div>
              </>
            ) : (
              <div className={classes.none}>Not asked</div>
            )}
          </Figure>
        );
      })}
    </section>
  );
};

/** One mark, its check and what it found on hover and to a screen reader. */
const MarkCell = ({ check, mark }: { check: Check; mark: Mark }) => {
  const classes = useStyles();
  const look = MARK[mark.state];
  const says = `${check.title}: ${look.word}. ${mark.detail}`;
  return (
    <td>
      <span
        className={classes.mark}
        style={{ color: look.colour }}
        title={says}
        aria-label={says}
        role="img"
      >
        {look.glyph}
      </span>
    </td>
  );
};

const Score = ({ service }: { service: ScoredService }) => {
  const classes = useStyles();
  const { passed, asked } = service;
  const tone = toneOf(passed, asked);
  return (
    <div className={classes.score}>
      <div className={classes.track} aria-hidden>
        <div
          className={classes.fill}
          style={{
            width: asked ? `${(passed / asked) * 100}%` : 0,
            background: TONE_COLOUR[tone],
          }}
        />
      </div>
      <span className={classes.scoreText}>
        <span aria-hidden>
          {passed}/{asked}
        </span>
        <span className={classes.hidden}>
          {passed} of {asked} checks passed
        </span>
      </span>
    </div>
  );
};

const Row = ({
  service,
  checks,
  highlighted,
}: {
  service: ScoredService;
  checks: Check[];
  highlighted: boolean;
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(highlighted);
  useEffect(() => {
    if (highlighted) setOpen(true);
  }, [highlighted]);
  const name = service.title ?? service.name;
  const id = rowIdOf(service.entityRef);
  return (
    <>
      <tr id={id} className={highlighted ? classes.highlighted : undefined}>
        <th scope="row" className={classes.serviceCell}>
          <div className={classes.serviceName}>
            <button
              type="button"
              className={classes.toggle}
              aria-expanded={open}
              aria-controls={`${id}-checks`}
              aria-label={`What the checks found for ${name}`}
              onClick={() => setOpen(o => !o)}
            >
              <span className={classes.chevron} data-open={open} aria-hidden>
                ›
              </span>
            </button>
            <Link to={entityPageOf(service.entityRef)}>{name}</Link>
          </div>
        </th>
        <td>
          <Score service={service} />
        </td>
        {checks.map(check => (
          <MarkCell
            key={check.id}
            check={check}
            mark={service.marks[check.id]}
          />
        ))}
      </tr>
      {open && (
        <tr id={`${id}-checks`}>
          <td colSpan={checks.length + 2}>
            <ul
              className={classes.details}
              aria-label={`What the checks found for ${name}`}
            >
              {checks.map(check => {
                const mark = service.marks[check.id];
                const look = MARK[mark.state];
                return (
                  <li key={check.id}>
                    <span style={{ color: look.colour }} aria-hidden>
                      {look.glyph}
                    </span>
                    <span className={classes.detailCheck}>
                      {check.title}
                      <span className={classes.hidden}> {look.word}</span>
                    </span>
                    <span>
                      {mark.link ? (
                        <Link to={mark.link}>{mark.detail}</Link>
                      ) : (
                        mark.detail
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </td>
        </tr>
      )}
    </>
  );
};

const Legend = ({ checks }: { checks: Check[] }) => {
  const classes = useStyles();
  return (
    <ol className={classes.legend} aria-label="Checks">
      {checks.map((check, i) => (
        <li key={check.id}>
          <b>{i + 1}</b>
          {check.title}
        </li>
      ))}
    </ol>
  );
};

const Table = ({
  checks,
  services,
}: {
  checks: Check[];
  services: ScoredService[];
}) => {
  const classes = useStyles();
  const { hash } = useLocation();
  const target = hash.replace(/^#/, '');

  // A service's facts bar links to its row: once the rows stand, bring it
  // into view, since the browser looked for it before they did.
  useEffect(() => {
    if (!target) return;
    document.getElementById(target)?.scrollIntoView?.({ block: 'center' });
  }, [target, services.length]);

  return (
    <div className={classes.tableBox}>
      <table className={classes.table} aria-label="Scorecards">
        <thead>
          <tr>
            <th scope="col">Service</th>
            <th scope="col">Score</th>
            {checks.map((check, i) => (
              <th
                key={check.id}
                scope="col"
                className={classes.checkHead}
                style={{ textAlign: 'center' }}
                title={check.title}
              >
                <span aria-hidden>{i + 1}</span>
                <span className={classes.hidden}>{check.title}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {services.map(service => (
            <Row
              key={service.entityRef}
              service={service}
              checks={checks}
              highlighted={rowIdOf(service.entityRef) === target}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ALL = '';
const NO_TEAM = '-';

type Order = 'name' | 'score';

/** A service's owner, when it is a team: the team filter offers these. */
const teamOf = (service: ScoredService) =>
  service.owner && /^group:/i.test(service.owner.ref)
    ? service.owner
    : undefined;

/** A score as a share; a service asked nothing sorts after every other. */
const shareOf = (service: ScoredService) =>
  service.asked ? service.passed / service.asked : Infinity;

/**
 * Narrowing and ordering the rows (specs/scorecards.md AC-10): by the
 * service's name, by its owning team, by a check it fails — and the lowest
 * score first, for a team looking for what to fix next. The scores, the
 * figures and the notices stay the catalog's whole; only the rows narrow.
 */
const Filters = ({
  checks,
  teams,
  query,
  team,
  failing,
  order,
  shown,
  total,
  onQuery,
  onTeam,
  onFailing,
  onOrder,
}: {
  checks: Check[];
  teams: { ref: string; label: string; count: number }[];
  query: string;
  team: string;
  failing: string;
  order: Order;
  shown: number;
  total: number;
  onQuery: (v: string) => void;
  onTeam: (v: string) => void;
  onFailing: (v: string) => void;
  onOrder: (v: Order) => void;
}) => {
  const classes = useStyles();
  return (
    <div className={classes.filters}>
      <TextField
        id="scorecards-service-filter"
        size="small"
        variant="outlined"
        label="Service"
        placeholder="Name"
        value={query}
        onChange={e => onQuery(e.target.value)}
        InputLabelProps={{ shrink: true }}
        className={classes.select}
        fullWidth
      />
      <TextField
        id="scorecards-team-filter"
        select
        size="small"
        variant="outlined"
        label="Owner team"
        value={team}
        onChange={e => onTeam(e.target.value)}
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
        className={classes.select}
        fullWidth
      >
        <option value={ALL}>All teams</option>
        {teams.map(t => (
          <option key={t.ref} value={t.ref}>
            {t.label} ({t.count})
          </option>
        ))}
        <option value={NO_TEAM}>No team as owner</option>
      </TextField>
      <TextField
        id="scorecards-failing-filter"
        select
        size="small"
        variant="outlined"
        label="Check"
        value={failing}
        onChange={e => onFailing(e.target.value)}
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
        className={classes.select}
        fullWidth
      >
        <option value={ALL}>Any outcome</option>
        {checks.map((check, i) => (
          <option key={check.id} value={check.id}>
            Fails {i + 1} · {check.title}
          </option>
        ))}
      </TextField>
      <TextField
        id="scorecards-order"
        select
        size="small"
        variant="outlined"
        label="Order"
        value={order}
        onChange={e => onOrder(e.target.value as Order)}
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
        className={classes.select}
        fullWidth
      >
        <option value="name">By name</option>
        <option value="score">Lowest score first</option>
      </TextField>
      <Typography className={classes.count} aria-live="polite">
        {shown === total
          ? `${total} ${total === 1 ? 'service' : 'services'}`
          : `${shown} of ${total} services`}
      </Typography>
    </div>
  );
};

/** The first tab: every service's score on the twelve checks. */
const ServicesView = () => {
  const classes = useStyles();
  const snapshot = useInsights<ScorecardsSnapshot>('/scorecards');
  const [query, setQuery] = useState('');
  const [team, setTeam] = useState(ALL);
  const [failing, setFailing] = useState(ALL);
  const [order, setOrder] = useState<Order>('name');

  const services = useMemo(
    () => snapshot.data?.services ?? [],
    [snapshot.data],
  );
  const teams = useMemo(() => {
    const byRef = new Map<
      string,
      { ref: string; label: string; count: number }
    >();
    for (const service of services) {
      const owner = teamOf(service);
      if (!owner) continue;
      const known = byRef.get(owner.ref);
      if (known) known.count += 1;
      else {
        byRef.set(owner.ref, {
          ref: owner.ref,
          label: owner.title ?? owner.ref.replace(/^group:(default\/)?/i, ''),
          count: 1,
        });
      }
    }
    return [...byRef.values()].sort((a, b) =>
      a.label.localeCompare(b.label, 'en', { sensitivity: 'base' }),
    );
  }, [services]);
  const rows = useMemo(() => {
    const words = query.trim().toLocaleLowerCase('en-US');
    const narrowed = services.filter(service => {
      const name = `${service.title ?? ''} ${service.name}`.toLocaleLowerCase(
        'en-US',
      );
      if (words && !name.includes(words)) return false;
      if (team === NO_TEAM && teamOf(service)) return false;
      if (team !== ALL && team !== NO_TEAM && teamOf(service)?.ref !== team) {
        return false;
      }
      if (failing !== ALL) {
        const mark = service.marks[failing as CheckId];
        if (mark?.state !== 'fail') return false;
      }
      return true;
    });
    // The backend orders by name; the lowest score first keeps that order
    // among equal scores.
    return order === 'score'
      ? [...narrowed].sort((a, b) => shareOf(a) - shareOf(b))
      : narrowed;
  }, [services, query, team, failing, order]);

  if (snapshot.loading) return <Progress />;
  if (snapshot.error || !snapshot.data) {
    return (
      <Typography role="alert">
        Couldn't load the scorecards from github-insights.
      </Typography>
    );
  }
  if (snapshot.data.generatedAt === null) {
    return (
      <Typography className={classes.message}>
        The checks have not run yet since the portal started. They run every
        half hour; check back in a moment.
      </Typography>
    );
  }
  const data = snapshot.data;
  const checkedAt = snapshot.data.generatedAt;
  const notices = data.sources
    .map(source => sourceNotice(source, data.checks))
    .filter((n): n is string => Boolean(n));
  const notCounted = data.sources.filter(source =>
    sourceNotice(source, data.checks),
  );
  const held = new Set(notCounted.flatMap(source => source.checks)).size;
  return (
    <div className={classes.stack}>
      <Figures snapshot={data} />
      <CollapsedNotices
        id="scorecards-not-counted"
        label="Not counted"
        summary={`${held} ${
          held === 1 ? 'check is' : 'checks are'
        } not counted for every service: ${notCounted.length} ${
          notCounted.length === 1 ? 'source has' : 'sources have'
        } not answered`}
        notices={notices}
      />
      {data.services.length > 0 ? (
        <>
          <Legend checks={data.checks} />
          <Filters
            checks={data.checks}
            teams={teams}
            query={query}
            team={team}
            failing={failing}
            order={order}
            shown={rows.length}
            total={data.services.length}
            onQuery={setQuery}
            onTeam={setTeam}
            onFailing={setFailing}
            onOrder={setOrder}
          />
          {rows.length > 0 ? (
            <Table checks={data.checks} services={rows} />
          ) : (
            <Typography className={classes.message}>
              No service matches these filters.
            </Typography>
          )}
        </>
      ) : (
        <Typography className={classes.message}>
          The catalog holds no service to score.
        </Typography>
      )}
      <Typography className={classes.sub}>
        Checked{' '}
        <time dateTime={checkedAt} title={checkedAt}>
          {updatedLabel(checkedAt)}
        </time>
        . How each check is decided, and how to pass it:{' '}
        <Link to="/docs/default/component/backstage/scorecards/">
          Scorecards
        </Link>
        .
      </Typography>
    </div>
  );
};

/** The two views of Scorecards, by the `view` the address names. */
type View = 'services' | 'repositories';

/**
 * Scorecards (DX-77; promised in specs/scorecards.md): how ready each service
 * in the catalog is for production, on twelve checks, the same for every one —
 * and, on its Repositories tab (DX-225, specs/repository-standard.md), whether
 * each repository meets the CI/CD standard.
 *
 * Which tab is open is in the address, `?view=repositories`, so a component's
 * CI/CD tab can lead to its repository's row, and a service's facts bar keeps
 * leading to its own on the first tab.
 *
 * Nothing is scored here. The github-insights backend asks the catalog, the
 * documentation count, the status page, the builds and the security findings,
 * and this page shows
 * what it answered — so a service's own page, which reads the same answer,
 * cannot show a different score.
 *
 * Three states say what they are rather than showing figures: the scores not
 * loaded, the checks not run yet since the portal started, and a source that
 * did not answer — whose checks count neither way, and are named above the
 * table rather than drawn as failures.
 */
export const ScorecardsPage = () => {
  const classes = useStyles();
  const [params, setParams] = useSearchParams();
  const view: View =
    params.get('view') === 'repositories' ? 'repositories' : 'services';
  return (
    <Page themeId="home">
      <Header
        title="Scorecards"
        subtitle="Production readiness across the catalog, and the CI/CD standard across the repositories."
      />
      <Content>
        <Tabs
          value={view}
          onChange={(_, next: View) =>
            setParams(next === 'services' ? {} : { view: next })
          }
          classes={{ root: classes.tabs }}
          indicatorColor="primary"
          aria-label="Scorecards views"
        >
          <Tab
            value="services"
            label="Services"
            classes={{ root: classes.tab, selected: classes.tabSelected }}
            disableRipple
          />
          <Tab
            value="repositories"
            label="Repositories"
            classes={{ root: classes.tab, selected: classes.tabSelected }}
            disableRipple
          />
        </Tabs>
        {view === 'repositories' ? <RepositoriesView /> : <ServicesView />}
      </Content>
    </Page>
  );
};
